import { Injectable } from '@angular/core';
import { Route, UrlSegment, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SettingsRoleEnum } from '../models/settings-role.enum';
import { pimAnfCodes } from '../models/settings';

@Injectable({
  providedIn: 'root',
})
export class RoleBasedRouteGuard  {
  constructor(private router: Router, private authService: AuthService) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.checkAccess(state.url);
  }

  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    const url = `/${route.path}`;
    return this.checkAccessForLoad(url);
  }

  /**
   * A login-farmacias token says which apps it opens. Without this app's permission the account
   * has no business here, whatever else the token allows; the API refuses it too. Returns false
   * when the session must be turned away, true when the ordinary role rules should decide.
   */
  private ssoAllows(): boolean {
    return !this.authService.isSso || this.authService.permissions.includes('bidsweb:access');
  }

  /**
   * Refuse a session this app is not for.
   *
   * The session has to end, not merely be turned away: LoginPageComponent sends a signed-in
   * visitor off the login page and back into the app, so a session left in place ping-pongs
   * between the two until the router gives up. `?error=access_denied` is what the login page
   * needs to say why, instead of returning an empty form. `terms_pending` says it more exactly when
   * the token's terms are pending, which is what withholds the permission (login-farmacias change
   * `terms-versions`, task 1.4). The claim is read before the logout, which forgets it.
   */
  private refuse(): string {
    const error = this.authService.termsPending ? 'terms_pending' : 'access_denied';
    this.authService.logout();
    return error;
  }

  private checkAccessForLoad(url: string): boolean {
    const settings = this.authService.userSettingsValue;
    const isLabRoute = url.startsWith('/lab');
    const isAccountRoute = url.startsWith('/account');
    const isLabUser = settings?.role === SettingsRoleEnum.lab;
    
    // Account routes are accessible to everyone
    if (isAccountRoute) {
      return true;
    }

    if (!this.ssoAllows()) {
      const error = this.refuse();
      this.router.navigate(['/account/login'], { queryParams: { error } });
      return false;
    }
    
    // Lab routes: only lab users can access
    if (isLabRoute) {
      if (isLabUser) {
        return true;
      }
      // Non-lab user trying to access lab route
      this.router.navigate(['/']);
      return false;
    }
    
    // Non-lab routes: only non-lab users can access
    if (isLabUser) {
      // Lab user trying to access non-lab route
      this.router.navigate(['/lab/home']);
      return false;
    }
    
    // Non-lab user accessing non-lab route
    // PIM routes: only when PIM is offered for some pharmacy -- `pimAccess`, or the role `dev`
    // on that pharmacy's own entry (G7; see pimAnfCodes)
    if (url.startsWith('/pim') && pimAnfCodes(settings).length === 0) {
      this.router.navigate(['/']);
      return false;
    }

    return true;
  }

  private checkAccess(url: string): boolean | UrlTree {
    const settings = this.authService.userSettingsValue;
    const isLabRoute = url.startsWith('/lab');
    const isAccountRoute = url.startsWith('/account');
    const isLabUser = settings?.role === SettingsRoleEnum.lab;

    // Account routes are accessible to everyone
    if (isAccountRoute) {
      return true;
    }

    if (!this.ssoAllows()) {
      const error = this.refuse();
      return this.router.createUrlTree(['/account/login'], {
        queryParams: { error },
      });
    }

    // Lab routes: only lab users can access
    if (isLabRoute) {
      if (isLabUser) {
        return true;
      }
      // Non-lab user trying to access lab route
      return this.router.createUrlTree(['/']);
    }

    // Non-lab routes: only non-lab users can access
    if (isLabUser) {
      // Lab user trying to access non-lab route
      return this.router.createUrlTree(['/lab/home']);
    }

    // Non-lab user accessing non-lab route
    // PIM routes: only when PIM is offered for some pharmacy -- `pimAccess`, or the role `dev`
    // on that pharmacy's own entry (G7; see pimAnfCodes)
    if (url.startsWith('/pim') && pimAnfCodes(settings).length === 0) {
      return this.router.createUrlTree(['/']);
    }

    return true;
  }
}

